import { useEffect, useState } from "react";
import { Download, FileSpreadsheet, Info } from "lucide-react";

import { FieldError } from "@/components/field-error";
import { PageHeader } from "@/components/page-header";
import { Panel } from "@/components/panel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { accountingFile, type AccountingProvider } from "@/lib/accounting-export.mjs";
import {
  generateComplianceReport,
  loadReportingSetup,
  recordAccountingExport,
} from "@/lib/api";
import { sha256Hex } from "@/lib/export-pack";
import type { Organisation } from "@/lib/types";
import { titleCase } from "@/lib/utils";

function iso(value: Date) {
  return value.toISOString().slice(0, 10);
}

export function AccountingBridge({ organisation }: { organisation: Organisation }) {
  const now = new Date();
  const [provider, setProvider] = useState<AccountingProvider>("tally");
  const [periodStart, setPeriodStart] = useState(iso(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))));
  const [periodEnd, setPeriodEnd] = useState(iso(now));
  const [country, setCountry] = useState("IN");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [lastRun, setLastRun] = useState<{ rows: number; sha256: string }>();

  useEffect(() => {
    loadReportingSetup(organisation.id)
      .then((setup) => setCountry(setup.profile?.country_code || "IN"))
      .catch((reason) => setError(reason.message));
  }, [organisation.id]);

  async function generate() {
    setBusy(true);
    setError("");
    try {
      const report = await generateComplianceReport(
        organisation.id,
        country,
        periodStart,
        periodEnd,
        "custom",
      );
      const file = accountingFile(provider, report);
      const sha256 = await sha256Hex(file.content);
      await recordAccountingExport(organisation.id, {
        provider,
        periodStart,
        periodEnd,
        rowCount: file.rowCount,
        sha256,
      });
      const url = URL.createObjectURL(new Blob([file.content], { type: file.mimeType }));
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `raqoz-${organisation.slug}-${provider}-${periodStart}-${periodEnd}.${file.extension}`;
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
      setLastRun({ rows: file.rowCount, sha256 });
    } catch (reasonValue) {
      setError((reasonValue as Error).message || "Unable to generate accounting file.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="ACCOUNTING BRIDGE"
        title="Accounting exports"
        description={`${organisation.name} · Carry approved invoice, tax, PO and payment references into your accounting system without re-keying the purchase register.`}
      />
      <section className="boundary-panel" aria-labelledby="accounting-boundary">
        <Info className="size-5" aria-hidden="true" />
        <div><h2 id="accounting-boundary">Explicit finance hand-off</h2><p>RAQOZ generates a provider-ready import file and records its digest. Finance reviews and imports it into the accounting system. This does not silently post books or move bank funds.</p></div>
      </section>
      <div className="grid gap-5 lg:grid-cols-[1fr_.8fr]">
        <Panel>
          <h2 className="text-xl font-semibold">Prepare import file</h2>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <Label>Accounting system<Select value={provider} onChange={(event) => setProvider(event.target.value as AccountingProvider)}><option value="tally">TallyPrime XML</option><option value="zoho_books">Zoho Books bills CSV</option><option value="quickbooks">QuickBooks bills CSV</option><option value="xero">Xero bills CSV</option></Select></Label>
            <Label>Reporting country<Input value={country} readOnly /></Label>
            <Label>Period start<Input type="date" value={periodStart} onChange={(event) => setPeriodStart(event.target.value)} /></Label>
            <Label>Period end<Input type="date" value={periodEnd} onChange={(event) => setPeriodEnd(event.target.value)} /></Label>
          </div>
          <FieldError>{error}</FieldError>
          <Button className="mt-5" disabled={busy || !periodStart || !periodEnd} onClick={generate}>{provider === "tally" ? <FileSpreadsheet className="size-4" /> : <Download className="size-4" />}{busy ? "Preparing…" : `Download ${titleCase(provider)} file`}</Button>
        </Panel>
        <Panel>
          <h2 className="text-xl font-semibold">Last generated file</h2>
          {!lastRun ? <p className="mt-3 text-sm text-[var(--text-secondary)]">No accounting file generated in this session.</p> : <><p className="mt-4 text-sm"><strong className="tabular-nums">{lastRun.rows}</strong> invoice rows included</p><p className="mt-5 text-xs text-[var(--text-secondary)]">SHA-256</p><code className="mt-1 block break-all text-xs">{lastRun.sha256}</code></>}
        </Panel>
      </div>
    </div>
  );
}
